/**
 * Public plan list (GET /subscription-plans/public), shared by the landing
 * page, sign-up and the plan screens: live prices per currency, limits,
 * and the currencies Eaze can take payment in. Falls back to the static
 * plans (constants/plans.ts) until — or if — the request fails.
 */
import { useQuery } from "@tanstack/react-query";
import api from "@/services/api";
import { LAUNCH_PLANS, mergeLivePlans, type LaunchPlan, type PublicApiPlan } from "@/constants/plans";

export interface PublicPlans {
  plans: LaunchPlan[];
  /** Currencies that can be charged right now (always includes GHS). */
  chargeable: string[];
  live: boolean;
}

export function usePublicPlans(base: LaunchPlan[] = LAUNCH_PLANS): PublicPlans {
  const q = useQuery({
    queryKey: ["public-plans"],
    queryFn: async () => {
      const res = await api.get("/subscription-plans/public");
      const data = res.data?.data ?? res.data?.plans ?? res.data;
      return { data: Array.isArray(data) ? (data as PublicApiPlan[]) : [], chargeable: Array.isArray(res.data?.chargeable) ? (res.data.chargeable as string[]) : ["GHS"] };
    },
    staleTime: 5 * 60_000,
    retry: 1,
  });
  return {
    plans: q.data ? mergeLivePlans(base, q.data.data) : base,
    chargeable: q.data?.chargeable?.length ? q.data.chargeable : ["GHS"],
    live: !!q.data,
  };
}

export default usePublicPlans;
